import { Box, Button, Divider, Paper, Tooltip, useMediaQuery } from "@mui/material"
import { useCallback, useEffect } from "react";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { Location } from 'history'
import { PageTitle } from "src/components/projetos/PageTitle"
import { BottomBar } from "src/framework/components/commons/BottomBar"
import theme from "src/theme/light";
import { MAIN_APP_CONTAINER_ID } from "src/framework/layouts/Main";
import { ProjetoPersonalizadoOffgridData } from "src/store/vendas/projetos/personalizado_offgrid/personalizadoOffgrid.types";
import DefaultLayout from "../../edicao/layouts/DefaultLayout";
import { ServicoCodeModal } from "../../containers/ServicoCodeModal";
import { ServicoInformationModal } from "../../containers/ServicoInformationModal";
import { useProjetoPersonalizadoOffgridStore } from "src/store/vendas/projetos/personalizado_offgrid/personalizadoOffgrid.store";

export interface LayoutProps {
  title: string;
  subtitle?: React.ReactNode;
  onClickBack: () => void;
  onClickNext: () => void;
  nextButtonDisabledTooltip?: string;
  nextButtonIsDisabled?: boolean;
  nextButtonLabel?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, title, nextButtonLabel = 'Continuar', nextButtonDisabledTooltip = '', subtitle, onClickBack, onClickNext, nextButtonIsDisabled = false }) => {
  const history = useHistory();
  const location = useLocation<{ allowAccess: boolean }>()

  const projetosState: ProjetoPersonalizadoOffgridData = useProjetoPersonalizadoOffgridStore(store => store.state.data);
  const { reset } = useProjetoPersonalizadoOffgridStore(store => store.actions);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const FooterButton = isMobile ? Box : BottomBar;

  const redirectToStart = useCallback(()=>{
    reset();
    history.push("/projetos?tipo=personalizado_offgrid", { done: true })
  },[history, reset]);

  useEffect(()=>{
    // Limpa estado da rota
    window.history.replaceState({}, document.title)
  },[location, location.state])

  useEffect(()=>{
    if (history.location.pathname.includes('edicao') && !projetosState?.codigo_venda) return;
    if (projetosState?.isEdit) return;

    // Evita usuário acessar fluxo de criação sem passar pelo formulário inicial..
    const { allowAccess = false } = location.state || {}
    if (!allowAccess) {
      redirectToStart()
    }
  },[projetosState, history, location, redirectToStart])

  useEffect(()=>{
    if (!projetosState) return;
    if (!projetosState.tipo) return;
    if (projetosState.isEdit) {
      const routeTipo = history.location.pathname.split('/')?.[4]
      if (!routeTipo) return;
      if (!routeTipo.includes(projetosState.tipo)) {
        redirectToStart()
      }
    }
  },[projetosState, history, history.location.pathname, redirectToStart])
  
  useEffect(()=>{
    const el = document.getElementById(MAIN_APP_CONTAINER_ID)
    if (el) el.scrollIntoView()
  },[title])

  const canNavigate = useCallback((promptLocation: Location | null = null)=>{
    const allowedLocations = ['/novo$', '/edicao$', 'clonar$']
    const loc = (promptLocation || history.location) as Location<{ done: boolean; noReset: boolean }>
    return loc.state?.noReset || loc.state?.done || allowedLocations.some(item => {
      return loc.pathname.match(new RegExp(item))
    })
  },[history.location])

  useEffect(()=>{
    return () => {
      if (!canNavigate()) {
        reset();
      }
    }
  },[canNavigate, reset])

  return (
    <DefaultLayout>
      <Paper>
        <Box p={2}>
          <PageTitle
            title={title}
            subtitle={subtitle || ''}
          />
        </Box>

        <Divider />
        {children}
      </Paper>
      <FooterButton style={isMobile ? { marginTop: 10, padding: 15 } : {}}>
        <Button
          variant="outlined"
          color={"secondary"}
          onClick={onClickBack}
          fullWidth={isMobile}
          style={isMobile ? { marginTop: 15 } : {}}
        >
          Voltar
        </Button>
        <Tooltip title={nextButtonIsDisabled ? nextButtonDisabledTooltip : ''}>
          <span>
            <Button
              disableElevation={true}
              disabled={nextButtonIsDisabled}
              color={"primary"}
              variant={"contained"}
              onClick={onClickNext}
              fullWidth={isMobile}
              style={isMobile ? { marginTop: 15, marginBottom: 15 } : {}}
              >
              {nextButtonLabel}
            </Button>
          </span>
        </Tooltip>
      </FooterButton>
      <ServicoCodeModal />
      <ServicoInformationModal formState={projetosState} />
      <Prompt message={(location) => {
        if (canNavigate(location)) return true;
        return `Deseja cancelar a ${projetosState?.isEdit ? 'edição' : 'criação'} do projeto?`
      }}/>
    </DefaultLayout>
  );
}

export { Layout }